// Shared shapes for the 850 and 852 dictionaries — the plain-English
// explanations the tool shows next to every parsed segment/element. Nothing
// in here is specific to any trading partner; it describes the generic X12
// 850 and 852 (5010) implementation guides only.

/**
 * Identifies which loop a *loop-defining* segment starts. Reused segments
 * that appear inside more than one loop (REF, PER, DTM, ...) don't carry a
 * loop of their own here — which loop they're actually nested in for a given
 * document is a structural fact the parser (lib/parser850.ts) determines from
 * segment order, using these ids to recognize where each loop begins.
 */
export enum LoopId {
  /** ISA/GS/ST/SE/GE/IEA — outside any transaction's own loops. */
  Envelope = 'envelope',
  /** The 850's header body, before the first N1 loop. */
  Header = 'header',
  /** An N1 (Name) loop, at either the header or the item level. */
  Name = 'name',
  /** A PO1 (baseline item) loop. */
  Item = 'item',
  /** The nested N1 sub-loop within a PO1 loop. */
  ItemName = 'itemName',
  /** The SLN sub-line loop nested within a PO1 loop. */
  SubLine = 'subLine',
  /** The TC1 transportation sub-loop nested within a PO1 loop. */
  Transportation = 'transportation',
  /** CTT/AMT — the transaction set's summary. */
  Summary = 'summary',
}

export interface CodeListEntry {
  code: string;
  label: string;
  /** The code list's own fuller explanation of the code, when it provides
   * one beyond the short label — e.g. the Product/Service ID Qualifier
   * list's per-code descriptions. Optional: most code lists in this
   * dictionary only need a label. When present, SegmentField.tsx shows it
   * as the coded value's tooltip in place of the short label. */
  description?: string;
}

export type ElementDataType =
  | 'string'
  | 'identifier'
  | 'code'
  | 'numeric'
  | 'date'
  | 'time';

export interface ElementDefinition {
  /** 1-indexed position within the segment, e.g. 2 for BEG02. */
  position: number;
  label: string;
  description: string;
  required?: boolean;
  dataType?: ElementDataType;
  /** Human-readable format hint shown next to the field and used by the
   * validator's format check, e.g. "CCYYMMDD" or "up to 2 decimal places". */
  format?: string;
  /** Present when this element's value is drawn from a fixed X12 code list.
   * The validator flags a value that isn't one of these as advisory-only. */
  codes?: CodeListEntry[];
}

export interface SegmentDefinition {
  id: string;
  name: string;
  purpose: string;
  loop: LoopId;
  /** Whether this segment is required wherever it's used (a missing required
   * segment is a validator finding, not a hard failure). */
  required?: boolean;
  /** Ordered by element position; index 0 need not be position 1 for a
   * segment whose lowest defined position is greater than 1. */
  elements: ElementDefinition[];
}

/** Returned by the dictionary lookup for a segment id it has no definition
 * for, so the UI always has *something* to render rather than branching on
 * `undefined` everywhere a lookup is used. */
export function unknownSegmentDefinition(id: string): SegmentDefinition {
  return {
    id,
    name: `Unrecognized segment (${id})`,
    purpose:
      'This segment is not in the generic X12 850 dictionary this tool ships with — it may be partner-specific or from a different transaction set.',
    loop: LoopId.Header,
    elements: [],
  };
}

export function unknownElementDefinition(
  segmentId: string,
  position: number
): ElementDefinition {
  return {
    position,
    label: `${segmentId}${String(position).padStart(2, '0')}`,
    description: 'No description available for this element.',
  };
}
